import { SeriesPieOptions } from 'highcharts'
import i18next from 'i18next'
import { VariableType } from '../../../../../components/Table/types'
import { CustomReportCategory } from '../../../../../redux/context/customReports/typesCategory'
import { CustomReportPieSection } from '../../../../../redux/context/customReports/typesSection'
import {
  CustomReportVariable,
  CustomReportVariableExplainer,
  ExplainerVariable
} from '../../../../../redux/context/customReports/typesVariable'
import { formatValueToNumberByType } from '../../../../../utils/helpers'
import { getTitleByLanguage } from '../customReportTable/utils'
import { DrillData, FormattedPoint } from './chartUtils'
import { Balance } from '../../../../../redux/context/reports/types'
import { resolveFormat } from '../grid/utils/valueFormatting'

export const getReferenceValue = (category: CustomReportCategory, balances?: Balance[]): number => {
  const { value: rootValue } = balances?.find(b => b.groupId === category.id) || {}
  const { value: leafValue } = balances?.find(b => b.groupId === category?.children?.[1]?.id) || {}
  return rootValue && leafValue ? leafValue / rootValue : 1
}

export const getPieVariableValue = (category: CustomReportCategory, balances?: Balance[]): number => {
  const { value: rootValue } = balances?.find(b => b.groupId === category.id) || {}
  const referenceValue = getReferenceValue(category, balances)
  return rootValue && referenceValue ? rootValue * referenceValue : 0
}

export const variablesToSeries = (
  variables: (CustomReportVariable | ExplainerVariable)[],
  category: CustomReportCategory,
  forceDrillDown?: boolean
): FormattedPoint[] => {
  return variables.map(variable => {
    const { balances } = variable
    const variableValue = getPieVariableValue(category, balances)
    const { color } = variable.style || {}
    // ExplainerVariable has no `type` field; fall back to absolute so the
    // formatter still produces a sensible string for explainer drill-down rows.
    const type = 'type' in variable ? variable.type : VariableType.absolute

    return {
      name: getTitleByLanguage(variable as CustomReportVariable) || '',
      drilldown: forceDrillDown || 'children' in variable ? variable.id?.toString() : undefined,
      y: variableValue ? Math.abs(variableValue) : null,
      negative: variableValue ? variableValue < 0 : false,
      color: forceDrillDown ? color : undefined,
      style: {
        fontWeight: variable.style?.fontWeight ? 'bold' : undefined,
        fontStyle: variable.style?.fontStyle
      },
      custom: resolveFormat({ type, style: variable.style }, undefined)
    }
  })
}

export const getLeafCategories = (categories: CustomReportCategory[]) => {
  const leafCategories: CustomReportCategory[] = []

  function recurse(data: CustomReportCategory[]) {
    if (!data) return
    data.forEach(category => {
      if (!category.children || category.type === 'function') leafCategories.push({ ...category })

      if (category.children && category.type !== 'function') {
        recurse(category.children)
      }
    })
  }

  recurse(categories)

  return leafCategories
}

export const getDrillData = (
  variableId: string,
  explainer: CustomReportVariableExplainer,
  categories: CustomReportCategory[]
): DrillData => {
  const leafs = getLeafCategories(categories)
  const category = leafs[0]
  const explanations: DrillData = {}

  const traverse = (varId: string, variables: (CustomReportVariable | ExplainerVariable)[]) => {
    if (!variables) return

    // Jos rivien määrä 1kpl silloin skipataan sarjan teko ja passataan edellinen id eteenpäin
    if (variables?.length === 1 && 'children' in variables[0]) {
      variables.forEach(row => {
        row.children && row.id && traverse(varId, row.children)
      })
    } else {
      const series = variablesToSeries(variables, category)
      explanations[varId.toString()] = series
      variables.forEach(row => {
        row.children && row.id && traverse(row.id.toString(), row.children)
      })
    }
  }

  traverse(variableId, explainer.variables)

  return explanations
}

export const getPieSeries = ({ name, categories, variables }: CustomReportPieSection): SeriesPieOptions => {
  const leafs = getLeafCategories(categories)
  const category = leafs[0]
  const onlyPercentage = variables?.every((row: CustomReportVariable) => row.type === VariableType.percentage)
  const referenceValue = getReferenceValue(category, variables[0]?.balances)
  const data = variablesToSeries(variables, category, true)
  let othersValue = referenceValue - data.reduce((acc, cur) => acc + (cur?.y || 0), 0)
  if (!onlyPercentage) {
    othersValue = formatValueToNumberByType(othersValue) || 0
  }
  const others = {
    name: i18next.t('dashboardPage:others') ?? 'Others',
    y: Math.abs(othersValue),
    negative: othersValue ? othersValue < 0 : false,
    custom: {
      numberFormat: { decimals: onlyPercentage ? 1 : 0 },
      variableType: onlyPercentage ? VariableType.percentage : variables[0]?.type ?? VariableType.absolute
    }
  } as FormattedPoint
  const pieSerie: SeriesPieOptions = {
    name,
    type: 'pie',
    data: referenceValue === 1 ? data : [others, ...data]
  }
  return pieSerie
}
